import { useEffect, type ReactNode } from 'react';
import { supabase } from '@/lib/supabase';
import { useIdleSignOut } from '@/hooks/useIdleSignOut';
import {
  clearPasswordRecoveryFlow,
  currentUrlHasPasswordRecoverySignal,
  markPasswordRecoveryFlow,
} from '@/lib/authRecovery';
import { useAuthStore } from '@/stores/authStore';
import { captureApplicationError } from '@/lib/monitoring';
import { toast } from '@/components/ui/toast';
import { SessionTimeoutDialog } from '@/components/auth/SessionTimeoutDialog';

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const session = useAuthStore((s) => s.session);
  const setSession = useAuthStore((s) => s.setSession);
  const setAuthError = useAuthStore((s) => s.setError);

  const idleSession = useIdleSignOut({ enabled: Boolean(session?.user) });

  useEffect(() => {
    let mounted = true;

    const urlHasPasswordRecoverySignal = currentUrlHasPasswordRecoverySignal();

    if (urlHasPasswordRecoverySignal) {
      markPasswordRecoveryFlow();
    }

    // Get initial session
    supabase.auth
      .getSession()
      .then(async ({ data: { session }, error }) => {
        if (!mounted) return;

        if (error) {
          captureApplicationError(error, {
            tags: { area: 'auth', operation: 'initial_session' },
          });
          await supabase.auth.signOut({ scope: 'local' }).catch((signOutError) => {
            captureApplicationError(signOutError, {
              tags: { area: 'auth', operation: 'local_sign_out_after_session_error' },
            });
          });
          setSession(null);
          setAuthError(AUTH_STARTUP_ERROR_MESSAGE);
          toast.error(AUTH_STARTUP_ERROR_MESSAGE);
          return;
        }

        if (session && (urlHasPasswordRecoverySignal || currentUrlHasPasswordRecoverySignal())) {
          markPasswordRecoveryFlow(session);
        }

        setAuthError(null);
        setSession(session);
      })
      .catch((error) => {
        if (mounted) {
          captureApplicationError(error, {
            tags: { area: 'auth', operation: 'initial_session_exception' },
          });
          setSession(null);
          setAuthError(AUTH_STARTUP_ERROR_MESSAGE);
          toast.error(AUTH_STARTUP_ERROR_MESSAGE);
        }
      });

    // Listen for auth state changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        markPasswordRecoveryFlow(session);
      }
      if (event === 'SIGNED_IN' && session && currentUrlHasPasswordRecoverySignal()) {
        markPasswordRecoveryFlow(session);
      }
      if (event === 'SIGNED_OUT') {
        clearPasswordRecoveryFlow();
      }
      if (session) {
        setAuthError(null);
      }
      setSession(session);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, [setAuthError, setSession]);

  return (
    <>
      {children}
      <SessionTimeoutDialog
        open={idleSession.warningOpen}
        remainingMs={idleSession.remainingMs}
        onStaySignedIn={idleSession.staySignedIn}
        onSignOut={() => void idleSession.signOutNow()}
      />
    </>
  );
}

const AUTH_STARTUP_ERROR_MESSAGE =
  'We could not restore your session. Please sign in again, or refresh the page if the issue continues.';
